import { PaymentTermsSettings } from '@/clients/finance/types/FinanceSettings';
import { BILLING_TYPE_SCHEMA, ExtendedOrganization, PRODUCT_CATEGORY } from '@schuettflix/interfaces';
import { z } from 'zod';

export const SummaryFinanceSettingsSchema = z.discriminatedUnion('billingMethod', [
    z.object({
        billingMethod: z.enum(BILLING_TYPE_SCHEMA.extract(['CREDIT_NOTE', 'INVOICE']).options, {
            message: 'order.summary.comboboxPlaceholder',
        }),
        paymentTermId: z.number({ message: 'order.summary.comboboxPlaceholder' }),
    }),
    z.object({
        billingMethod: z.enum(BILLING_TYPE_SCHEMA.extract(['PARTNER_INVOICE']).options, {
            message: 'order.summary.comboboxPlaceholder',
        }),
        paymentTermId: z.null(),
    }),
]);

export type SummaryFinanceSettings = z.infer<typeof SummaryFinanceSettingsSchema>;

type PossiblePaymentTerms = {
    supplierPaymentTermId: number;
    carrierPaymentTermId: number;
};

type GenerateSummaryFinanceSettingsDefaultValuesReturnType = {
    defaultValues?: Partial<SummaryFinanceSettings>;
    possiblePaymentTerms?: PossiblePaymentTerms;
};

type GenerateSummaryFinanceSettingsDefaultValuesParams = {
    paymentTermsSettings: PaymentTermsSettings;
    addressedOrganization: Pick<
        ExtendedOrganization,
        'supplierPaymentTerm' | 'carrierPaymentTerm' | 'clientPaymentTerm' | 'isPartnerInvoicingEnabled'
    >;
    includedProductCategories: PRODUCT_CATEGORY[];
    isRevenueIncreasingCase: boolean;
};

export function generateSummaryFinanceSettingsDefaultValues({
    paymentTermsSettings,
    addressedOrganization: { supplierPaymentTerm, carrierPaymentTerm, clientPaymentTerm, isPartnerInvoicingEnabled },
    isRevenueIncreasingCase,
    includedProductCategories,
}: GenerateSummaryFinanceSettingsDefaultValuesParams): GenerateSummaryFinanceSettingsDefaultValuesReturnType {
    const defaultBillingMethod = isRevenueIncreasingCase ? 'INVOICE' : 'CREDIT_NOTE';
    let defaultValues: Partial<SummaryFinanceSettings> | undefined;
    let possiblePaymentTerms: PossiblePaymentTerms | undefined = undefined;

    if (isRevenueIncreasingCase) {
        if (clientPaymentTerm) {
            defaultValues = {
                paymentTermId: clientPaymentTerm.id,
                billingMethod: defaultBillingMethod,
            };
        } else {
            defaultValues = {
                paymentTermId: paymentTermsSettings.marketDefaults.revenueIncrease,
                billingMethod: defaultBillingMethod,
            };
        }

        return { defaultValues };
    }

    if (isPartnerInvoicingEnabled) {
        defaultValues = {
            paymentTermId: null,
            billingMethod: 'PARTNER_INVOICE',
        };

        return { defaultValues };
    }

    const hasNoTransportProducts = includedProductCategories.every(category => category !== 'TRANSPORT');
    if (hasNoTransportProducts) {
        if (supplierPaymentTerm) {
            defaultValues = {
                paymentTermId: supplierPaymentTerm.id,
                billingMethod: defaultBillingMethod,
            };
        } else {
            defaultValues = {
                paymentTermId: paymentTermsSettings.marketDefaults.costIncrease,
                billingMethod: defaultBillingMethod,
            };
        }

        return { defaultValues };
    }

    const hasNoMaterialProducts = includedProductCategories.every(category => category !== 'MATERIAL');
    if (hasNoMaterialProducts) {
        if (carrierPaymentTerm) {
            defaultValues = {
                paymentTermId: carrierPaymentTerm.id,
                billingMethod: defaultBillingMethod,
            };
        } else {
            defaultValues = {
                paymentTermId: paymentTermsSettings.marketDefaults.costIncrease,
                billingMethod: defaultBillingMethod,
            };
        }

        return { defaultValues };
    }

    if (supplierPaymentTerm && carrierPaymentTerm) {
        if (supplierPaymentTerm.id === carrierPaymentTerm.id) {
            defaultValues = {
                paymentTermId: supplierPaymentTerm.id,
                billingMethod: defaultBillingMethod,
            };
        } else {
            defaultValues = {
                paymentTermId: supplierPaymentTerm.id,
                billingMethod: defaultBillingMethod,
            };

            possiblePaymentTerms = {
                supplierPaymentTermId: supplierPaymentTerm.id,
                carrierPaymentTermId: carrierPaymentTerm.id,
            };
        }

        return { defaultValues, possiblePaymentTerms };
    }

    const fallbackPaymentTermId =
        supplierPaymentTerm?.id ?? carrierPaymentTerm?.id ?? paymentTermsSettings.marketDefaults.costIncrease;

    if (fallbackPaymentTermId) {
        defaultValues = {
            paymentTermId: fallbackPaymentTermId,
            billingMethod: defaultBillingMethod,
        };

        return { defaultValues };
    }

    return { defaultValues: undefined, possiblePaymentTerms: undefined };
}
